import type { ComponentProps } from 'react';
import React, { useImperativeHandle } from 'react';

import { cn } from '../cn';
import { buttonHPadding, isMacLike } from '../common';
import { ButtonPropsSizeType } from '../common.type';

export type TextareaValueProps = Omit<ComponentProps<'textarea'>, 'size'> & {
  size?: ButtonPropsSizeType;
  error?: string;
  rows?: number;
  onCmdClickSubmit?: (e: React.SyntheticEvent<Element, Event>) => void;
};

export default function TextareaValue({
  className,
  value,
  defaultValue,
  error,
  size,
  rows = 1,
  onCmdClickSubmit,
  ref,
  ...props
}: TextareaValueProps) {
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);

  useImperativeHandle(ref, () => inputRef.current!);

  const adjustHeight = React.useCallback(() => {
    if (inputRef.current) {
      inputRef.current.style.height = '0px';
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  }, []);

  React.useLayoutEffect(() => {
    adjustHeight();
  }, [adjustHeight, value]);

  return (
    <textarea
      ref={inputRef}
      rows={rows}
      onInput={adjustHeight}
      onKeyDown={(e) => {
        if ((isMacLike() ? e.metaKey : e.ctrlKey) && e.key === 'Enter' && onCmdClickSubmit) {
          e.preventDefault();
          onCmdClickSubmit(e);
        }
      }}
      className={cn(
        'm-[-1px] h-full shrink grow resize-none bg-transparent outline-none',
        buttonHPadding(size),
        {
          'py-2.5': size == 'base' || size == null,
          'py-1.5': size == 'sm',
          'py-1': size == 'xs',
        },
        { 'placeholder-fair-pink-600/20 dark:placeholder-fair-pink-600/20': error },
        className,
      )}
      value={value}
      defaultValue={defaultValue}
      {...props}
    />
  );
}
